import { useEffect, useState } from 'react';
import db from '@/db';
import { toast } from 'sonner';
import { Vault, Lock, Unlock } from 'lucide-react';

/**
 * Toggle para exigir sessão de caixa aberta antes de criar comandas novas.
 * Quando ativo, todas as criações via balança/leitor passam por
 * `assertSessionOpenIfRequired()` que lança erro com `NO_OPEN_SESSION`.
 */
export default function RequireSessionCard() {
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const cfg = await db.config.get('requireOpenSession');
      setEnabled(!!cfg?.value);
    })();
  }, []);

  const toggle = async () => {
    setSaving(true);
    try {
      const next = !enabled;
      await db.config.put({ key: 'requireOpenSession', value: next });
      setEnabled(next);
      toast.success(next ? 'Bloqueio ativado' : 'Bloqueio desativado', {
        description: next
          ? 'A partir de agora, comandas novas exigem caixa aberto.'
          : 'Comandas podem ser criadas mesmo com caixa fechado.',
      });
    } catch (err) {
      toast.error('Falha ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-require-session" className={`bg-white border-2 rounded-2xl p-6 ${enabled ? 'border-emerald-200' : 'border-slate-200'}`}>
      <div className="flex items-start gap-4">
        <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
          enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
        }`}>
          <Vault size={22} strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
            Exigir caixa aberto
            {enabled ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                <Unlock size={10} /> Ativo
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">
                <Lock size={10} /> Inativo
              </span>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-1.5 max-w-prose">
            Quando ativo, comandas novas só podem ser criadas (via balança, etiqueta ou leitor) se houver uma sessão de caixa aberta. Comandas já abertas continuam editáveis normalmente.
          </p>
          <p className="text-[11px] text-slate-400 mt-2">
            <strong>Recomendado</strong> para times com mais de um operador, força fechar/abrir caixa por turno e melhora conciliação financeira.
          </p>
        </div>

        <button
          data-testid="toggle-require-session"
          onClick={toggle}
          disabled={saving}
          role="switch"
          aria-checked={enabled}
          className={`shrink-0 relative w-14 h-8 rounded-full transition-colors duration-200 disabled:opacity-50 ${
            enabled ? 'bg-emerald-600' : 'bg-slate-300'
          }`}
        >
          <span
            className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow transform transition-transform duration-200 ${
              enabled ? 'translate-x-6' : 'translate-x-0'
            }`}
          />
        </button>
      </div>
    </div>
  );
}
